import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
    this.closest('product-form-component')?.syncColorConfirmation?.();
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;
    const productForm = this.closest('product-form-component');
    if (productForm?.colorConfirmationBlocked) {
      event.preventDefault();
      productForm.syncColorConfirmation();
      return;
    }
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart && !quantitySelector.canAddToCart().canAdd) return;
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (image) preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;
    if (!cartIcon || !addToCartButton || !image) return;
    const flyToCartElement = document.createElement('fly-to-cart');
    const flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick' : 'fly-to-cart--main';
    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
    addToCartButton.dataset.added = 'true';
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) this.#resetTimeouts.splice(index, 1);
    }, 800);
    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;
  #variantChangeInProgress = false;
  #variantChangeGeneration = 0;
  #addToCartQueue = [];
  #pendingVariantChange = null;
  #originalLabels = new WeakMap();

  #confirmationState() {
    const picker = this.#getVariantPicker();
    if (!picker?.requiresColorConfirmation) return { active: false, ready: true, message: '' };
    const message = picker.confirmationMessage;
    const loading = picker.confirmationLoading || this.#variantChangeInProgress;
    let resource = null;
    try {
      const text = picker.querySelector('script[type="application/json"]')?.textContent;
      resource = text ? JSON.parse(text) : null;
    } catch {
      resource = null;
    }
    const matchesId = resource?.id != null && String(resource.id) === String(this.#getVariantIdInput());
    // Verify the server's resolved variant also matches each explicitly selected radio.
    const matchesOptions = Array.from(picker.querySelectorAll('fieldset input:checked')).every((input) => {
      const position = Number.parseInt(input.dataset.inputId?.split('-')[0] || '', 10);
      return Number.isInteger(position) && resource?.options?.[position - 1] === input.value;
    });
    let ready = !message && !loading && resource?.available === true && matchesId && matchesOptions;
    if (ready && this.refs.quantitySelector?.canAddToCart) {
      ready = this.refs.quantitySelector.canAddToCart().canAdd;
    }
    return { active: true, ready, message, loading, resource };
  }

  get colorConfirmationBlocked() {
    const state = this.#confirmationState();
    return state.active && !state.ready;
  }

  syncColorConfirmation() {
    const state = this.#confirmationState();
    if (!state.active) return;
    for (const container of this.querySelectorAll('add-to-cart-component')) {
      const button = container.refs?.addToCartButton;
      if (!button) continue;
      const label = button.querySelector('[data-color-confirmation-label]');
      if (label) {
        if (!this.#originalLabels.has(label)) {
          this.#originalLabels.set(label, label.textContent.trim());
        }
        const original = this.#originalLabels.get(label);
        label.textContent = state.message || (state.loading ? 'PLEASE WAIT' : original);
      }
      button.disabled = !state.ready;
    }
    const accelerated = this.refs.acceleratedCheckoutButtonContainer;
    if (accelerated) accelerated.hidden = !state.ready;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('change', () => this.syncColorConfirmation(), { signal });
    queueMicrotask(() => {
      if (this.isConnected) this.syncColorConfirmation();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return this.querySelector('input[name="id"]')?.value;
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' }, credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;
    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;
    const quantitySelector = this.querySelector('quantity-selector-component');
    if (quantitySelector?.setCartQuantity) quantitySelector.setCartQuantity(cartQty);
    this.#updateQuantityLabel(cartQty);
    this.syncColorConfirmation();
  }

  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;
    event.promise?.then(({ detail }) => {
      if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;
      if (detail?.items) this.#updateCartQuantity({ items: detail.items });
      else return this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
    });
  };

  handleSubmit(event) {
    event.preventDefault();
    if (this.colorConfirmationBlocked) {
      this.syncColorConfirmation();
      const picker = this.#getVariantPicker();
      this.#setLiveRegionText(picker?.confirmationMessage || 'Please wait for your selection to load.');
      return;
    }
    if (this.#variantChangeInProgress) {
      this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }
    this.#processAddToCart(undefined, undefined, event);
  }

  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;
    return {
      quantity: this.#getQuantity(), generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.colorConfirmationBlocked) {
      this.syncColorConfirmation();
      return;
    }
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);
    const allAddToCartContainers = this.querySelectorAll('add-to-cart-component');
    if (!overrideVariantId && Array.from(allAddToCartContainers).some((container) => container.refs.addToCartButton?.disabled)) return;
    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');
    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) container.disable();
        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) textNode.textContent = errorMessage;
          else addToCartTextError.appendChild(document.createTextNode(errorMessage));
          this.#setLiveRegionText(errorMessage);
          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }
        setTimeout(() => {
          for (const container of allAddToCartContainers) container.enable();
          this.syncColorConfirmation();
        }, ERROR_BUTTON_REENABLE_DELAY);
        return;
      }
    }
    const formData = new FormData(form);
    if (overrideVariantId) formData.set('id', overrideVariantId);
    if (overrideQuantity !== undefined) formData.set('quantity', overrideQuantity.toString());
    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }
    formData.set('sections', sectionIds.join(','));
    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: [{ merchandiseId: formData.get('id'), quantity: itemCount }],
      promise: deferredEventPromise.promise,
    }));
    const fetchCfg = fetchConfig('javascript', { body: formData });
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg, headers: { ...fetchCfg.headers, Accept: 'text/html' },
    }).then((response) => response.json()).then(async (response) => {
      if (response.status) {
        this.dispatchEvent(new CartErrorEvent({
          error: response.message || 'Add to cart failed', code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        }));
        this.#refreshCart().then((ajaxCart) => deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true, items: ajaxCart.items, source: 'product-form-component',
            sourceId: this.id.toString(), itemCount, productId: this.dataset.productId,
          },
        })).catch(deferredEventPromise.reject);
        this.#showCartError(response.message);
        return;
      }
      const id = formData.get('id');
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }
      if (!id) throw new Error('Form ID is required');
      this.#announceAdded(allAddToCartContainers[0]?.refs.addToCartButton);
      await this.#refreshCart().then((ajaxCart) => {
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items, source: 'product-form-component', sourceId: this.id.toString(),
            itemCount, productId: this.dataset.productId, sections: response.sections, didError: false,
          },
        });
        if (this.#getVariantIdInput()) this.#updateCartQuantity(ajaxCart);
        return ajaxCart;
      }).catch(deferredEventPromise.reject);
    }).catch((error) => {
      console.error(error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE',
      }));
    }).finally(() => {
      this.syncColorConfirmation();
      if (event) cartPerformance.measureFromEvent('add:user-action', event);
    });
  }

  #showCartError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;
    addToCartTextError.classList.remove('hidden');
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) textNode.textContent = message;
    else addToCartTextError.appendChild(document.createTextNode(message));
    this.#setLiveRegionText(message);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #announceAdded(button) {
    if (!button) return;
    const text = button.querySelector('.add-to-cart-text--added')?.textContent?.trim() || Theme.translations.added;
    this.#setLiveRegionText(text);
    setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  #processBatchAddToCart(items) {
    if (items.length === 0 || this.colorConfirmationBlocked) return;
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);
    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    }));
    const payload = {
      items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })),
      sections: sectionIds.join(','),
    };
    fetch(Theme.routes.cart_add_url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then((response) => response.json()).then(async (response) => {
      if (response.status) {
        this.dispatchEvent(new CartErrorEvent({
          error: response.message || 'Add to cart failed', code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        }));
        this.#refreshCart().then((ajaxCart) => deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true, items: ajaxCart.items, source: 'product-form-component',
            sourceId: this.id.toString(), itemCount: totalQuantity, productId: this.dataset.productId,
          },
        })).catch(deferredEventPromise.reject);
        this.#showCartError(response.message);
        return;
      }
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }
      const containers = this.querySelectorAll('add-to-cart-component');
      this.#announceAdded(containers[0]?.refs.addToCartButton);
      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items, source: 'product-form-component', sourceId: this.id.toString(),
          itemCount: totalQuantity, productId: this.dataset.productId, sections: response.sections, didError: false,
        },
      });
      this.#updateCartQuantity(cart);
    }).catch((error) => {
      console.error(error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE',
      }));
    });
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  #setLiveRegionText(text) { this.refs.liveRegion.textContent = text; }
  #clearLiveRegionText() { this.refs.liveRegion.textContent = ''; }

  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) morph(currentElement, newElement);
    else if (currentElement && !newElement) currentElement.remove();
    else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', newElement.cloneNode(true));
    }
  }

  #onProductSelect = async (event) => {
    const sourceCard = event.target?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    this.#pendingVariantChange = event.promise;
    this.syncColorConfirmation();
    try {
      const { detail } = await event.promise;
      if (generation !== this.#variantChangeGeneration || !detail?.html) return;
      const { html, newProduct, productId, resource } = detail;
      if (newProduct) this.dataset.productId = newProduct.id;
      else if (productId && productId !== this.dataset.productId) return;
      this.refs.variantId.value = resource?.id ?? '';
      const { addToCartButtonContainer: currentContainer, acceleratedCheckoutButtonContainer } = this.refs;
      const currentButton = currentContainer?.refs.addToCartButton;
      if (!currentContainer || (!currentButton && !acceleratedCheckoutButtonContainer)) return;
      const newButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newButton && currentButton) {
        // Forget the old label so the newly rendered native text is captured.
        const oldLabel = currentButton.querySelector('[data-color-confirmation-label]');
        if (oldLabel) this.#originalLabels.delete(oldLabel);
        morph(currentButton, newButton);
      }
      if (resource == null || resource.available == false) currentContainer.disable();
      else currentContainer.enable();
      if (acceleratedCheckoutButtonContainer) {
        acceleratedCheckoutButtonContainer.hidden = resource == null || resource.available == false;
      }
      if (resource) {
        const media = resource.featured_media?.preview_image?.src;
        if (media) this.refs.addToCartButtonContainer?.setAttribute('data-product-variant-media', media + '&width=100');
      }
      const { quantityRules, pricePerItem, quantitySelector, productFormButtons, quantityLabel, quantitySelectorWrapper } = this.refs;
      const newQuantityInput = html.querySelector('quantity-selector-component input[ref="quantityInput"]');
      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }
      const newQuantityRules = html.querySelector('.quantity-rules');
      const newPricePerItem = html.querySelector('price-per-item');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;
      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        const currentQuantityValue = quantitySelector.getValue?.();
        const newProductFormButtons = html.querySelector('.product-form-buttons');
        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);
          const newInput = html.querySelector('quantity-selector-component input[ref="quantityInput"]');
          if (this.refs.quantitySelector?.updateConstraints && newInput && currentQuantityValue) {
            this.refs.quantitySelector.setValue(currentQuantityValue);
            this.refs.quantitySelector.updateConstraints(newInput.min, newInput.max || null, newInput.step);
            this.dataset.quantityDefault = newInput.min || '1';
          }
        }
      } else {
        const targets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];
        for (const [selector, currentElement, fallback] of targets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);
      this.syncColorConfirmation();
      const hasB2BFeatures = quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;
      if (hasB2BFeatures) {
        this.#refreshCart().then((cart) => this.#updateCartQuantity(cart)).catch((error) => console.warn(error));
      }
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('[product-form] Variant update failed:', error);
    } finally {
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
        this.syncColorConfirmation();
        if (this.#getVariantPicker()?.requiresColorConfirmation) this.#addToCartQueue = [];
        else await this.#drainAddToCartQueue();
      }
    }
  };

  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;
    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];
    const resolvedItems = [];
    for (const item of queuedItems) {
      const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
      if (resolvedItem) resolvedItems.push(resolvedItem);
    }
    this.#processBatchAddToCart(resolvedItems);
  }

  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) return null;
    return { variantId, quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints) };
  }

  async #resolveQueuedVariant(item) {
    let resolvedVariantId = null;
    let available;
    let quantityConstraints = null;
    if (item.pendingVariantChange) {
      try {
        const result = await item.pendingVariantChange;
        const resource = result?.detail?.resource;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) available = false;
        else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch {
        const resolved = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null, available: false, quantityConstraints: null,
        }));
        resolvedVariantId = resolved.variantId;
        available = resolved.available;
        quantityConstraints = resolved.quantityConstraints;
      }
    }
    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId, intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });
    return { variantId, quantityConstraints };
  }

  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;
    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : Math.max(max - cartQuantity, min);
    let normalizedQuantity = quantity;
    if ((quantity - min) % step !== 0) normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
    return Math.max(min, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
  }

  #getQuantityConstraintsFromHtml(html) {
    const input = html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null;
    if (!input) return null;
    return { min: input.min, max: input.max || null, step: input.step, cartQuantity: input.getAttribute('data-cart-quantity') };
  }

  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };
    const response = await fetch(variantResolutionUrl, { credentials: 'same-origin' });
    if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const text = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!text) return { variantId: null, available: false, quantityConstraints };
    const resource = JSON.parse(text);
    if (!resource || typeof resource !== 'object') return { variantId: null, available: false, quantityConstraints };
    return { variantId: resource.id != null ? String(resource.id) : null, available: resource.available !== false, quantityConstraints };
  }

  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? []);
    const productId = this.dataset.productId;
    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }
    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  #isAddToCartDisabled() {
    return Array.from(this.querySelectorAll('add-to-cart-component')).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
